import React, { useContext, useEffect, useState, useMemo } from 'react'
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PointElement, LineElement } from 'chart.js'
import { Bar, Doughnut, Pie } from 'react-chartjs-2'
import CountUp from 'react-countup'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import AuthContext from 'context/AuthContext'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import helpers from 'utils/helpers'

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend)

function Home() {
  const { t } = useTranslation()
  const { logoutUser, updatePageName } = useContext(AuthContext)
  const [dashboardDetails, setDashboardDetails] = useState({})
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState('')
  const [isReset, setIsReset] = useState(false)

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' }
    },
    scales: { y: { beginAtZero: true } }
  }), [])

  // Original pie options for other charts
  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const status = dashboardDetails?.transactionStats?.byStatus?.[context.dataIndex]
            if (status) {
              return [
                `${t('COUNT')}: ${status.count}`,
                `${t('AMOUNT')}: $${status.totalAmount?.toLocaleString('en-US', { 
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2 
                }) || '0.00'}`
              ]
            }
            return `${context.label}: ${context.raw}`
          }
        }
      }
    }
  }), [dashboardDetails?.transactionStats?.byStatus, t])

  const handleApiError = error => {
    console.error('error ', error)
    if (error.response?.status === 401 || error.response?.status === 409) logoutUser()
  }

  const getDashboardDetails = async () => {
    try {
      const payload = {
        startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format('YYYY-MM-DD'), null),
        endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format('YYYY-MM-DD'), null)
      }
      const result = await apiGet(apiPath.getDashboardDetails, payload)
      setDashboardDetails(prev => ({ ...prev, ...result.data.results }))
    } catch (error) { handleApiError(error) }
  }

  useEffect(() => { getDashboardDetails() }, [startDate, endDate])
  useEffect(() => { updatePageName(t('NAV_DASHBOARD')) }, [updatePageName, t])

  const handleDateChange = (start, end) => { setStartDate(start); setEndDate(end) }
  const handleReset = () => { setEndDate(''); setStartDate(''); setIsReset(true) }

  const charts = useMemo(() => {
    const data = dashboardDetails
    const colors = {
      blue: ['rgba(54, 162, 235, 0.6)', 'rgba(54, 162, 235, 1)'],
      green: ['rgba(75, 192, 192, 0.6)', 'rgba(75, 192, 192, 1)'],
      red: ['rgba(255, 99, 132, 0.6)', 'rgba(255, 99, 132, 1)'],
      orange: ['rgba(255, 159, 64, 0.6)', 'rgba(255, 159, 64, 1)'],
      purple: ['rgba(153, 102, 255, 0.6)', 'rgba(153, 102, 255, 1)'],
      yellow: ['rgba(255, 205, 86, 0.6)', 'rgba(255, 205, 86, 1)'],
      teal: ['rgba(72, 187, 120, 0.6)', 'rgba(72, 187, 120, 1)']
    }

    return {
      companyStatus: {
        labels: [t('TOTAL_COMPANIES'), t('ACTIVE_COMPANIES'), t('INACTIVE_COMPANIES')],
        datasets: [{
          label: t('COMPANY_STATUS'),
          data: [data?.totalCompany?.totalCompany || 0, data?.totalCompany?.activeCompany || 0, data?.totalCompany?.inactiveCompany || 0],
          backgroundColor: [colors.blue[0], colors.green[0], colors.red[0]],
          borderColor: [colors.blue[1], colors.green[1], colors.red[1]],
          borderWidth: 1
        }]
      },
      complaints: {
        labels: [t('PENDING'), t('CLOSED')],
        datasets: [{
          data: [data?.totalPendingComplaints || 0, data?.totalClosedComplaints || 0],
          backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(255, 205, 86, 0.6)'],
          borderColor: ['rgba(255, 99, 132, 1)', 'rgba(255, 205, 86, 1)'],
          borderWidth: 2,
          // Store amounts as metadata for easy access
          metadata: {
            amounts: [
              data?.pendingComplaintsAmount || 0,
              data?.closedComplaintsAmount || 0
            ]
          }
        }]
      },
      location: {
        labels: [t('COUNTRIES'), t('STATES')],
        datasets: [
          { label: t('O_TOTAL'), data: [data?.totalCountries?.totalCountries || 0, data?.totalStates?.totalStates || 0], backgroundColor: colors.blue[0], borderColor: colors.blue[1], borderWidth: 1 },
          { label: t('ACTIVE'), data: [data?.totalCountries?.activeCountries || 0, data?.totalStates?.activeStates || 0], backgroundColor: colors.green[0], borderColor: colors.green[1], borderWidth: 1 },
          { label: t('INACTIVE'), data: [data?.totalCountries?.inactiveCountries || 0, data?.totalStates?.inactiveStates || 0], backgroundColor: colors.red[0], borderColor: colors.red[1], borderWidth: 1 }
        ]
      },
      categoryVsSub: {
        labels: [t('CATEGORIES'), t('SUBCATEGORIES')],
        datasets: [
          { label: t('O_TOTAL'), data: [data?.totalCategoriesData?.totalCategories || 0, data?.totalSubCategoriesData?.totalSubCategories || 0], backgroundColor: colors.purple[0], borderColor: colors.purple[1], borderWidth: 1 },
          { label: t('ACTIVE'), data: [data?.totalCategoriesData?.activeCategories || 0, data?.totalSubCategoriesData?.activeSubCategories || 0], backgroundColor: colors.orange[0], borderColor: colors.orange[1], borderWidth: 1 },
          { label: t('INACTIVE'), data: [data?.totalCategoriesData?.inactiveCategories || 0, data?.totalSubCategoriesData?.inactiveSubCategories || 0], backgroundColor: colors.red[0], borderColor: colors.red[1], borderWidth: 1 }
        ]
      },
      subscription: {
        labels: [ t('TOTAL_SUBSCRIPTIONS'), t('ACTIVE_SUBSCRIPTIONS'), t('INACTIVE_SUBSCRIPTIONS'),],
        datasets: [{
          label: t('SUBSCRIPTION_STATUS'),
          data: [
            (data?.totalActiveSuscriptionCreated || 0) + (data?.totalInActiveSuscriptionCreated || 0),
            data?.totalActiveSuscriptionCreated || 0,
            data?.totalInActiveSuscriptionCreated || 0,
          ],
          backgroundColor: [ colors.blue[0], colors.green[0], colors.red[0],],
          borderColor: [colors.blue[1], colors.green[1], colors.red[1]],
          borderWidth: 1
        }]
      },
      transactionStatus: {
        labels: data?.transactionStats?.byStatus?.map(item => t(item._id.toUpperCase())) || [],
        datasets: [{
          data: data?.transactionStats?.byStatus?.map(item => item.count) || [],
          backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 205, 86, 0.6)', 'rgba(255, 99, 132, 0.6)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 205, 86, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 2
        }]
      },
      supportData: {
        labels: [t('TOTAL_SUPPORT'), t('PENDING_SUPPORT'), t('CLOSED_SUPPORT')],
        datasets: [{
          label: t('SUPPORT_TICKETS'),
          data: [
            data?.totalSupportData?.totalSupport || 0,
            data?.totalSupportData?.pendingSupport || 0,
            data?.totalSupportData?.closedSupport || 0
          ],
          backgroundColor: [colors.teal[0], colors.yellow[0], colors.green[0]],
          borderColor: [colors.teal[1], colors.yellow[1], colors.green[1]],
          borderWidth: 1
        }]
      },
      formsData: {
        labels: [t('TOTAL_FORMS'), t('HIRING_FORMS'), t('ONBOARDING_FORMS')],
        datasets: [{
          label: t('FORMS_DISTRIBUTION'),
          data: [
            data?.totalFormsData?.totalForms || 0,
            data?.totalFormsData?.totalHiringForms || 0,
            data?.totalFormsData?.totalOnboardingForms || 0
          ],
          backgroundColor: [colors.purple[0], colors.orange[0], colors.blue[0]],
          borderColor: [colors.purple[1], colors.orange[1], colors.blue[1]],
          borderWidth: 1
        }]
      }
    }
  }, [dashboardDetails, t])

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-100',
      textLight: 'text-blue-700',
      textDark: 'text-blue-900'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-100',
      textLight: 'text-green-700',
      textDark: 'text-green-900'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-100',
      textLight: 'text-purple-700',
      textDark: 'text-purple-900'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      textLight: 'text-amber-700',
      textDark: 'text-amber-900'
    },
    pink: {
      bg: 'bg-pink-50',
      border: 'border-pink-100',
      textLight: 'text-pink-700',
      textDark: 'text-pink-900'
    },
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-100',
      textLight: 'text-indigo-700',
      textDark: 'text-indigo-900'
    },
    red: { // Added red color
      bg: 'bg-red-50',
      border: 'border-red-100',
      textLight: 'text-red-700',
      textDark: 'text-red-900'
    }
  }


  const summaryCards = useMemo(() => [
    { title: t('ACTIVE_COMPANIES'), value: dashboardDetails?.totalCompany?.activeCompany || 0, color: 'blue', link: '/company-manager' },
    { title: t('ACTIVE_COUNTRIES'), value: dashboardDetails?.totalCountries?.activeCountries || 0, color: 'green', link: '/country_manager' },
    { title: t('ACTIVE_STATES'), value: dashboardDetails?.totalStates?.activeStates || 0, color: 'purple', link: '/state_manager' },
    { title: t('ACTIVE_CATEGORIES'), value: dashboardDetails?.totalCategoriesData?.activeCategories || 0, color: 'amber', link: '/category-subcategory-manager' },
    { title: t('ACTIVE_SUBCATEGORIES'), value: dashboardDetails?.totalSubCategoriesData?.activeSubCategories || 0, color: 'pink', link: '/category-subcategory-manager' },
    { title: t('ACTIVE_SUBSCRIPTIONS'), value: dashboardDetails?.totalActiveSuscriptionCreated || 0, color: 'indigo', link: '/subscription_manager' }
  ], [dashboardDetails, t])

  const transactionSummary = useMemo(() => ({
    completed: dashboardDetails?.transactionStats?.byStatus?.find(s => s._id === 'completed')?.count || 0,
    pending: dashboardDetails?.transactionStats?.byStatus?.find(s => s._id === 'pending')?.count || 0,
    completedAmount: dashboardDetails?.transactionStats?.byStatus?.find(s => s._id === 'completed')?.totalAmount || 0,
    pendingAmount: dashboardDetails?.transactionStats?.byStatus?.find(s => s._id === 'pending')?.totalAmount || 0
  }), [dashboardDetails])

  const ChartCard = ({ title, children, chartType = 'Bar', customOptions }) => (
    <div className='bg-white p-4 rounded-lg border shadow-sm'>
      <h3 className='text-lg font-semibold mb-4'>{title}</h3>
      <div className='h-64'>
        {chartType === 'Bar' && <Bar data={children} options={customOptions || chartOptions} />}
        {chartType === 'Doughnut' && <Doughnut data={children} options={customOptions || pieOptions} />}
        {chartType === 'Pie' && <Pie data={children} options={customOptions || pieOptions} />}
      </div>
    </div>
  )

  return (
    <>
      <div className='sm:flex items-center text-center sm:text-left px-3 md:px-4 xl:px-7 lg:px-5 md:py-5 py-4 md:py-8 border'>
        <ODateRangePicker handleDateChange={handleDateChange} isReset={isReset} setIsReset={setIsReset} />
        <button 
          type='button' 
          onClick={handleReset} 
          className='bg-LightBlue text-sm px-8 mb-3 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2 transition duration-200'
        >
          {t('O_RESET')}
        </button>
      </div>
      
      <div className='py-4 px-4 md:px-8'>
        <div className='mb-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white shadow-lg'>
          <div className='flex flex-col md:flex-row md:items-center justify-between'>
            <div>
              <h2 className='text-xl font-semibold mb-2'>{t('TOTAL_EARNING_TILL_DATE')}</h2>
              <p className='text-3xl font-bold'>
                ${dashboardDetails?.totalEarningTillDate ? 
                  <CountUp 
                    duration={0.8} 
                    end={dashboardDetails.totalEarningTillDate} 
                    separator=',' 
                    decimals={2}
                  /> : '0.00'}
              </p>
            </div>
            <div className='mt-4 md:mt-0'>
              <p className='text-sm opacity-90'>{t('TRANSACTION_SUMMARY')}</p>
              <div className='flex gap-6 mt-2'>
                <div className='text-center'>
                  <p className='text-lg font-semibold'>{transactionSummary.completed}</p>
                  <p className='text-xs opacity-90'>{t('COMPLETED')}</p>
                  <p className='text-xs opacity-90 mt-1'>${transactionSummary.completedAmount?.toLocaleString('en-US', { 
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2 
                  }) || '0.00'}</p>
                </div>
                <div className='text-center'>
                  <p className='text-lg font-semibold'>{transactionSummary.pending}</p>
                  <p className='text-xs opacity-90'>{t('PENDING')}</p>
                  <p className='text-xs opacity-90 mt-1'>${transactionSummary.pendingAmount?.toLocaleString('en-US', { 
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2 
                  }) || '0.00'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-7'>
          {summaryCards.map((card, index) => {
            const colorClass = colorClasses[card.color] || colorClasses.blue;
            
            return (
              <Link to={card.link} key={index}>
                <div 
                  className={`${colorClass.bg} h-[100px] p-4 rounded-lg border ${colorClass.border} hover:shadow-md transition duration-200`}
                >
                  <h4 className={`text-sm ${colorClass.textLight} font-medium mb-1`}>
                    {card.title}
                  </h4>
                  <p className={`text-xl font-bold ${colorClass.textDark}`}>
                    <CountUp duration={0.5} end={card.value} />
                  </p>
                </div>
              </Link>
            )
          })}
        </div>
        
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-7'>
          <ChartCard title={t('COMPANY_STATUS_OVERVIEW')}>{charts.companyStatus}</ChartCard>
          {/* <ChartCard title={t('COMPLAINTS_DISTRIBUTION')} chartType='Doughnut' customOptions={complaintsOptions}>{charts.complaints}</ChartCard> */}
          <ChartCard title={t('TRANSACTION_STATUS_DISTRIBUTION')} chartType='Pie'>{charts.transactionStatus}</ChartCard>
          <ChartCard title={t('SUBSCRIPTION_STATUS')}>{charts.subscription}</ChartCard>
          <ChartCard title={t('SUPPORT_TICKETS_OVERVIEW')}>{charts.supportData}</ChartCard>
          <ChartCard title={t('LOCATION_STATISTICS')}>{charts.location}</ChartCard>
          <ChartCard title={t('CATEGORY_VS_SUBCATEGORY')}>{charts.categoryVsSub}</ChartCard>
          <ChartCard title={t('FORMS_DISTRIBUTION_OVERVIEW')}>{charts.formsData}</ChartCard>
        </div>
      </div>
    </>
  )
}

export default Home